import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import type { AttendanceRule, ThresholdRule, MissedShiftRule } from '../settings/types';

export interface PointsSettingsState {
  // Points expiry
  pointsExpiryDays: number;

  // Attendance rules toggles
  lateArrivalsEnabled: boolean;
  earlyDeparturesEnabled: boolean;
  missedShiftEnabled: boolean;

  // Attendance rules data
  lateArrivalRules: AttendanceRule[];
  earlyDepartureRules: AttendanceRule[];
  missedShiftRules: MissedShiftRule[];

  // Threshold rules
  thresholdRules: ThresholdRule[];
}
interface RootState {
  pointsSettings: PointsSettingsState;
}

const initialState: PointsSettingsState = {
  pointsExpiryDays: 20,

  lateArrivalsEnabled: true,
  earlyDeparturesEnabled: true,
  missedShiftEnabled: true,

  lateArrivalRules: [
    { id: '1', minMinutes: 5, maxMinutes: 10, points: 1, applicableDays: 'all_days', shiftDuration: 'any_shift' },
    { id: '2', minMinutes: 10, maxMinutes: 30, points: 2, applicableDays: 'all_days', shiftDuration: 'any_shift' },
    { id: '3', minMinutes: 30, maxMinutes: 60, points: 3, applicableDays: 'all_days', shiftDuration: 'any_shift' },
  ],
  earlyDepartureRules: [
    { id: '1', minMinutes: 5, maxMinutes: 10, points: 1, applicableDays: 'all_days', shiftDuration: 'any_shift' },
    { id: '2', minMinutes: 10, maxMinutes: 30, points: 2, applicableDays: 'all_days', shiftDuration: 'any_shift' },
    { id: '3', minMinutes: 30, maxMinutes: 60, points: 3, applicableDays: 'all_days', shiftDuration: 'any_shift' },
  ],
  missedShiftRules: [
    { id: '1', absentType: 'one_day', points: 5, shiftDuration: 'any_shift' },
    { id: '2', absentType: 'multiple_days', points: 10, shiftDuration: 'any_shift', minDays: 2, maxDays: 4 },
  ],

  thresholdRules: [
    { id: '1', points: 5, notificationAction: 'notify_employee_manager', escalationAction: 'no_action' },
    { id: '2', points: 10, notificationAction: 'notify_employee_manager_admin', escalationAction: 'disciplinary_note' },
    {
      id: '3',
      points: 15,
      notificationAction: 'notify_employee_manager_admin',
      escalationAction: 'probation_review',
      probationLevel: 'probation_review_1',
    },
  ],
};

const pointsSettingsSlice = createSlice({
  name: 'pointsSettings',
  initialState,
  reducers: {
    // Points expiry actions
    setPointsExpiryDays: (state, action: PayloadAction<number>) => {
      state.pointsExpiryDays = action.payload;
    },

    // Attendance rule toggle actions
    setLateArrivalsEnabled: (state, action: PayloadAction<boolean>) => {
      state.lateArrivalsEnabled = action.payload;
    },
    setEarlyDeparturesEnabled: (state, action: PayloadAction<boolean>) => {
      state.earlyDeparturesEnabled = action.payload;
    },
    setMissedShiftEnabled: (state, action: PayloadAction<boolean>) => {
      state.missedShiftEnabled = action.payload;
    },

    // Missed shift actions
    addMissedShiftRule: (state, action: PayloadAction<MissedShiftRule>) => {
      state.missedShiftRules.push(action.payload);
    },
    removeMissedShiftRule: (state, action: PayloadAction<string>) => {
      state.missedShiftRules = state.missedShiftRules.filter((rule) => rule.id !== action.payload);
    },
    updateMissedShiftRule: (
      state,
      action: PayloadAction<{
        id: string;
        field: keyof MissedShiftRule;
        value: number | string;
      }>
    ) => {
      const { id, field, value } = action.payload;
      const ruleIndex = state.missedShiftRules.findIndex((r) => r.id === id);
      if (ruleIndex !== -1) {
        state.missedShiftRules[ruleIndex] = {
          ...state.missedShiftRules[ruleIndex],
          [field]: value,
        };
      }
    },

    // Late arrival rules actions
    addLateArrivalRule: (state, action: PayloadAction<AttendanceRule>) => {
      state.lateArrivalRules.push(action.payload);
    },
    removeLateArrivalRule: (state, action: PayloadAction<string>) => {
      state.lateArrivalRules = state.lateArrivalRules.filter((rule) => rule.id !== action.payload);
    },
    updateLateArrivalRule: (
      state,
      action: PayloadAction<{
        id: string;
        field: keyof AttendanceRule;
        value: number | string;
      }>
    ) => {
      const { id, field, value } = action.payload;
      const ruleIndex = state.lateArrivalRules.findIndex((r) => r.id === id);
      if (ruleIndex !== -1) {
        state.lateArrivalRules[ruleIndex] = {
          ...state.lateArrivalRules[ruleIndex],
          [field]: value,
        };
      }
    },

    // Early departure rules actions
    addEarlyDepartureRule: (state, action: PayloadAction<AttendanceRule>) => {
      state.earlyDepartureRules.push(action.payload);
    },
    removeEarlyDepartureRule: (state, action: PayloadAction<string>) => {
      state.earlyDepartureRules = state.earlyDepartureRules.filter((rule) => rule.id !== action.payload);
    },
    updateEarlyDepartureRule: (
      state,
      action: PayloadAction<{
        id: string;
        field: keyof AttendanceRule;
        value: number | string;
      }>
    ) => {
      const { id, field, value } = action.payload;
      const ruleIndex = state.earlyDepartureRules.findIndex((r) => r.id === id);
      if (ruleIndex !== -1) {
        state.earlyDepartureRules[ruleIndex] = {
          ...state.earlyDepartureRules[ruleIndex],
          [field]: value,
        };
      }
    },

    // Threshold rules actions
    addThresholdRule: (state, action: PayloadAction<ThresholdRule>) => {
      state.thresholdRules.push(action.payload);
    },
    removeThresholdRule: (state, action: PayloadAction<string>) => {
      state.thresholdRules = state.thresholdRules.filter((rule) => rule.id !== action.payload);
    },
    updateThresholdRule: (
      state,
      action: PayloadAction<{
        id: string;
        field: keyof ThresholdRule;
        value: string | number;
      }>
    ) => {
      const { id, field, value } = action.payload;
      const ruleIndex = state.thresholdRules.findIndex((r) => r.id === id);
      if (ruleIndex !== -1) {
        state.thresholdRules[ruleIndex] = {
          ...state.thresholdRules[ruleIndex],
          [field]: value,
        };
      }
    },
  },
});

export const {
  setPointsExpiryDays,
  setLateArrivalsEnabled,
  setEarlyDeparturesEnabled,
  setMissedShiftEnabled,
  addLateArrivalRule,
  removeLateArrivalRule,
  updateLateArrivalRule,
  addEarlyDepartureRule,
  removeEarlyDepartureRule,
  updateEarlyDepartureRule,
  addMissedShiftRule,
  removeMissedShiftRule,
  updateMissedShiftRule,
  addThresholdRule,
  removeThresholdRule,
  updateThresholdRule,
} = pointsSettingsSlice.actions;

// Selectors
export const usePointsSettingsState = () => useSelector((state: RootState) => state.pointsSettings);

// Individual selectors for performance
export const usePointsExpiryDays = () => useSelector((state: RootState) => state.pointsSettings.pointsExpiryDays);
export const useLateArrivalsEnabled = () => useSelector((state: RootState) => state.pointsSettings.lateArrivalsEnabled);
export const useEarlyDeparturesEnabled = () =>
  useSelector((state: RootState) => state.pointsSettings.earlyDeparturesEnabled);
export const useMissedShiftEnabled = () => useSelector((state: RootState) => state.pointsSettings.missedShiftEnabled);
export const useLateArrivalRules = () => useSelector((state: RootState) => state.pointsSettings.lateArrivalRules);
export const useEarlyDepartureRules = () => useSelector((state: RootState) => state.pointsSettings.earlyDepartureRules);
export const useMissedShiftRules = () => useSelector((state: RootState) => state.pointsSettings.missedShiftRules);
export const useThresholdRules = () => useSelector((state: RootState) => state.pointsSettings.thresholdRules);

export default pointsSettingsSlice.reducer;
